/**
 * Shared datetime utilities — the single source of truth for the policy:
 *   • UTC for all stored instants
 *   • CalendarDate (YYYY-MM-DD) for date-only fields — never converted to UTC instant
 *   • localCalendarDate() to bucket instants into local calendar days
 *   • formatCalendarDate() to display a CalendarDate without tz-shift off-by-one
 */

/** A bare calendar date with no time or timezone: "YYYY-MM-DD". */
export type CalendarDate = string;

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True when the string is a bare YYYY-MM-DD with no time component. */
export function isCalendarDate(s: string): boolean {
  return CALENDAR_DATE_RE.test(s);
}

/**
 * Normalises an unknown date-like string to a CalendarDate ("YYYY-MM-DD").
 * Accepts both bare YYYY-MM-DD and full ISO strings; returns undefined for
 * anything unparseable.
 *
 * For a full ISO instant, pass `timezone` so the date is down-converted to the
 * patient's LOCAL calendar day (the correct behaviour for a date-only field — a
 * model that emits `2019-06-15T23:00:00-05:00` means June 15, not the UTC June
 * 16). Without a timezone it falls back to the UTC date portion.
 */
export function toCalendarDate(v: unknown, timezone?: string): CalendarDate | undefined {
  if (typeof v !== "string" || !v.trim()) return undefined;
  const s = v.trim();
  if (isCalendarDate(s)) return s;
  // Full ISO — convert to the patient-local calendar day when a tz is supplied,
  // otherwise take the UTC date portion (first 10 chars of toISOString).
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return undefined;
  return timezone ? localCalendarDate(s, timezone) : d.toISOString().slice(0, 10);
}

/**
 * Local calendar date ("YYYY-MM-DD") of a UTC instant in the given IANA timezone.
 * Use this to bucket events into local calendar days (D3 fix).
 *
 * This is the same pattern used correctly in sleep.ts for nightKey.
 */
export function localCalendarDate(isoInstant: string, timezone: string): CalendarDate {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(isoInstant));
  } catch {
    return isoInstant.slice(0, 10);
  }
}

/**
 * Converts a CalendarDate to a UTC instant at local noon in the given timezone.
 *
 * Using noon (not midnight) avoids DST transitions (which typically occur at
 * 02:00) from flipping the calendar day.  Use ONLY to derive a sortable
 * `occurredAt` instant from a user-supplied calendar date — never use this
 * for display (use formatCalendarDate instead).
 */
export function calendarDateToUtcInstant(date: CalendarDate, timezone: string): string {
  try {
    const [yearStr, monthStr, dayStr] = date.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!Number.isFinite(year + month + day)) return `${date}T12:00:00.000Z`;

    // Probe instant: noon UTC on this date. Noon keeps us far from any DST edge.
    const approxUtcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

    // What wall-clock time (INCLUDING the calendar date) does that instant show
    // in the target tz? Reading the full date — not just the hour — is what makes
    // this correct for offsets beyond ±12h (UTC+12/+13/+14, e.g. New Zealand,
    // Samoa, Kiribati), where noon-UTC falls on the *next* local day.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    }).formatToParts(approxUtcNoon);
    const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value);

    // Some engines render midnight as "24"; normalise to 00:00 of the next day.
    let localHour = get("hour");
    let dayCarry = 0;
    if (localHour === 24) {
      localHour = 0;
      dayCarry = 1;
    }

    // Treat the local wall-clock reading as if it were UTC, then diff against the
    // probe → the tz offset (with the day component folded in, so it's exact).
    const localAsUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day") + dayCarry,
      localHour,
      get("minute"),
      get("second")
    );
    const offsetMs = localAsUtc - approxUtcNoon.getTime();

    // Shift the probe so the resulting UTC instant maps to noon local.
    return new Date(approxUtcNoon.getTime() - offsetMs).toISOString();
  } catch {
    return `${date}T12:00:00.000Z`;
  }
}

/**
 * Y/M/D/H/Mi/S broken out for `date` as displayed in `tz`.
 *
 * Consolidated from api/src/lib/suggestions.ts (F-04, DATETIME_AUDIT_2026-09-20.md)
 * so every timezone conversion in the app runs through this one seam (T6).
 */
export interface LocalDateParts {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

/** Local wall-clock parts of `date` in IANA zone `tz`. */
export function localDateParts(date: Date, tz: string): LocalDateParts {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const m: Record<string, string> = {};
  for (const p of f.formatToParts(date)) if (p.type !== "literal") m[p.type] = p.value;
  const h = Number(m.hour);
  return {
    y: Number(m.year),
    mo: Number(m.month),
    d: Number(m.day),
    h: h === 24 ? 0 : h, // some engines emit "24" for midnight
    mi: Number(m.minute),
    s: Number(m.second),
  };
}

/** Offset (ms) such that `wallClockAsUTC - offset === actualUTC` for `tz` at `date`. */
export function tzOffsetMs(date: Date, tz: string): number {
  const p = localDateParts(date, tz);
  const asUTC = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  return asUTC - date.getTime();
}

/** Minutes since local midnight for `date` in `tz` (0-1439). */
export function localMinutesOfDay(date: Date, tz: string): number {
  const p = localDateParts(date, tz);
  return p.h * 60 + p.mi;
}

/**
 * ISO instant for local `hh:mm` on the given "YYYY-MM-DD" `date` in `tz`.
 *
 * Generalizes `calendarDateToUtcInstant` (which hardcodes noon) to an arbitrary
 * time-of-day. Starts from the same noon-UTC probe — noon keeps the *initial*
 * offset lookup far from any DST edge — then makes a second pass, because the
 * noon offset is not necessarily the offset at the target time-of-day: on a
 * transition day the two straddle the change. Applying noon's post-transition
 * offset to a pre-transition 00:00 lands an hour off, and for US zones on the
 * spring-forward day it lands on the PREVIOUS calendar day, which is precisely
 * the off-by-one this module exists to prevent. The correction re-reads the
 * offset at the candidate instant and, if it differs from the probe's, redoes
 * the arithmetic with it.
 *
 * DST edge cases resolve the conventional way: a wall-clock time inside the
 * spring-forward gap (which never occurs) yields the instant one offset-step
 * later, and an ambiguous fall-back time (which occurs twice) yields the first
 * of the two.
 */
export function calendarDateTimeToUtcInstant(
  date: CalendarDate,
  tz: string,
  hh: number,
  mm: number
): string {
  const [yearStr, monthStr, dayStr] = date.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const wallClockAsUtc = Date.UTC(year, month - 1, day, hh, mm, 0);

  // Pass 1: offset from a noon-UTC probe on the target day.
  const probeOffset = tzOffsetMs(new Date(Date.UTC(year, month - 1, day, 12, 0, 0)), tz);
  const candidate = new Date(wallClockAsUtc - probeOffset);

  // Pass 2: the offset actually in force at the candidate instant. Equal to the
  // probe's on all but the two transition days a year, so this is normally a no-op.
  const actualOffset = tzOffsetMs(candidate, tz);
  if (actualOffset === probeOffset) return candidate.toISOString();
  return new Date(wallClockAsUtc - actualOffset).toISOString();
}

/**
 * Formats a CalendarDate (or legacy full-ISO string) for display WITHOUT any
 * timezone conversion.
 *
 * The key rule: "2024-06-15" should always render as June 15, 2024, regardless
 * of the viewer's timezone.  We achieve this by interpreting the date as UTC
 * date parts and then formatting in UTC — so the browser's local offset never
 * shifts the day.
 *
 * Also handles legacy records stored as a full ISO "YYYY-MM-DDT00:00:00.000Z":
 * the UTC date portion is extracted and displayed the same way.
 */
export function formatCalendarDate(
  date: string | undefined | null,
  opts: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" }
): string {
  if (!date) return "";
  // Extract the YYYY-MM-DD portion regardless of whether it's a full ISO or bare date.
  const bare = date.length >= 10 ? date.slice(0, 10) : date;
  if (!CALENDAR_DATE_RE.test(bare)) return date; // unknown format, return as-is

  const [yearStr, monthStr, dayStr] = bare.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isFinite(year + month + day)) return date;

  // Construct a UTC Date at midnight then format in UTC so no local shift occurs.
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  return utcDate.toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
}

/**
 * Returns today's date as a CalendarDate in the given IANA timezone.
 * In the browser, call with Intl.DateTimeFormat().resolvedOptions().timeZone.
 */
export function todayInTimezone(timezone: string): CalendarDate {
  return localCalendarDate(new Date().toISOString(), timezone);
}

/**
 * Whole calendar days from `from` to `to` (to − from), both bare YYYY-MM-DD
 * CalendarDates. Positive when `to` is later. No timezone or DST math — this is
 * pure date arithmetic on the date parts, which is the correct model for
 * date-only fields (onsetDate, resolvedAt). Returns NaN if either side is not a
 * parseable calendar date.
 */
export function calendarDaysBetween(from: CalendarDate, to: CalendarDate): number {
  const a = from.length >= 10 ? from.slice(0, 10) : from;
  const b = to.length >= 10 ? to.slice(0, 10) : to;
  if (!CALENDAR_DATE_RE.test(a) || !CALENDAR_DATE_RE.test(b)) return NaN;
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const MS_PER_DAY = 86_400_000;
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / MS_PER_DAY);
}

/** CalendarDate plus `days` whole days (negative to go back). Pure date arithmetic
 *  on the Y-M-D parts (T10), no zone and no DST. Returns the input unchanged if it
 *  is not a parseable CalendarDate. */
export function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const d = date.length >= 10 ? date.slice(0, 10) : date;
  if (!CALENDAR_DATE_RE.test(d)) return date;
  const [y, m, dd] = d.split("-").map(Number);
  // UTC is correct here: a CalendarDate has no zone, so UTC is just a calendar engine.
  const t = new Date(Date.UTC(y, m - 1, dd + days));
  const yy = String(t.getUTCFullYear()).padStart(4, "0");
  const mm = String(t.getUTCMonth() + 1).padStart(2, "0");
  const ddd = String(t.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${ddd}`;
}

/** Day of week of a CalendarDate, 0 = Sunday ... 6 = Saturday. NaN if unparseable. */
export function calendarDayOfWeek(date: CalendarDate): number {
  const d = date.length >= 10 ? date.slice(0, 10) : date;
  if (!CALENDAR_DATE_RE.test(d)) return NaN;
  const [y, m, dd] = d.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, dd)).getUTCDay();
}
